import React, { useState, useEffect } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const About: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [headerRef, headerVisible] = useScrollAnimation(0.3);
  const [leftRef, leftVisible] = useScrollAnimation(0.2);
  const [rightRef, rightVisible] = useScrollAnimation(0.2);
  const [expertiseRef, expertiseVisible] = useScrollAnimation(0.3);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const achievements = [
    { number: '500+', label: 'Campaigns Delivered', icon: '🚀' },
    { number: '50+', label: 'Happy Clients', icon: '⭐' },
    { number: '15+', label: 'Years Experience', icon: '🎯' },
    { number: '99%', label: 'Client Satisfaction', icon: '💯' }
  ];

  const whyChooseUs = [
    {
      icon: '🏆',
      title: 'Proven Expertise',
      description: 'Top-tier brands trust us with their most important campaigns',
      color: 'from-brand-orange/20 to-brand-gold/20'
    },
    {
      icon: '🎨',
      title: 'ATL + BTL Mastery',
      description: 'Complete marketing solutions under one roof',
      color: 'from-brand-gold/20 to-brand-orange/20'
    },
    {
      icon: '💡',
      title: 'Innovative Concepts',
      description: 'Creative ideas with flawless execution',
      color: 'from-brand-orange/20 to-brand-gold/20'
    },
    {
      icon: '🌍',
      title: 'Global Standards',
      description: 'Nationwide presence with international quality',
      color: 'from-brand-gold/20 to-brand-orange/20'
    }
  ];

  return (
    <section id="about" className="relative py-12 sm:py-16 lg:py-20 overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-white">
        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-5">
          <div className="h-full w-full" style={{
            backgroundImage: `
              linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-16 h-16 bg-brand-orange/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-12 h-12 bg-brand-gold/15 rounded-full blur-lg animate-bounce"></div>
        <div className="absolute bottom-40 left-1/4 w-20 h-20 bg-brand-orange/5 rounded-full blur-2xl animate-ping"></div>
        <div className="absolute bottom-20 right-1/3 w-8 h-8 bg-brand-gold/20 rounded-full blur-md animate-pulse"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 sm:mb-20 transition-all duration-1000 ${
            headerVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Badge */}
          <div className={`inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm border border-brand-orange/20 rounded-full px-4 py-2 text-brand-orange text-sm font-medium mb-6 transition-all duration-1000 ${
            isLoaded ? 'animate-fade-in-up' : ''
          }`} style={{ animationDelay: '0.1s' }}>
            <div className="w-2 h-2 bg-brand-orange rounded-full animate-pulse"></div>
            <span>Our Story</span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 sm:mb-8 transition-all duration-700">
            <span className={`block transition-all duration-500 ${
              isLoaded ? 'animate-fade-in-up' : ''
            }`} style={{ animationDelay: '0.2s' }}>
              About Us
            </span>
            <span className={`block bg-gradient-to-r from-brand-orange to-brand-gold bg-clip-text text-transparent transition-all duration-500 ${
              isLoaded ? 'animate-fade-in-up' : ''
            }`} style={{ animationDelay: '0.4s' }}>
              Aviation Excellence
            </span>
          </h2>
          
          <div className={`w-24 h-1 bg-gradient-to-r from-brand-orange to-brand-gold mx-auto mb-6 sm:mb-8 transition-all duration-500 ${
            isLoaded ? 'animate-fade-in-up' : ''
          }`} style={{ animationDelay: '0.6s' }}></div>
          
          <p className={`text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed transition-all duration-500 ${
            isLoaded ? 'animate-fade-in-up' : ''
          }`} style={{ animationDelay: '0.8s' }}>
            At Brand Aviation, we don't just host events —we create experiences that make your brand soar higher for impact.
          </p>
        </div>

        {/* Enhanced Main Content */}
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center mb-16 sm:mb-20">
          {/* Left Content - Story */}
          <div 
            ref={leftRef}
            className={`space-y-8 transition-all duration-1000 ${
              leftVisible 
                ? 'opacity-100 translate-x-0' 
                : 'opacity-0 -translate-x-10'
            }`}
          >
            {/* Story Card */}
            <div className="relative overflow-hidden">
              {/* Card Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-brand-orange/20 to-brand-gold/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10 scale-110"></div>
              
              {/* Main Card */}
              <div className="relative bg-white/95 backdrop-blur-sm border border-white/30 rounded-3xl p-8 sm:p-10 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-brand-orange/20 group-hover:scale-105">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-brand-orange to-brand-gold rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4 shadow-lg">
                    ✈️
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 transition-all duration-500">
                    Our Mission
                  </h3>
                </div>
                
                <div className="space-y-6 text-center">
                  <p className="text-lg sm:text-xl text-gray-600 leading-relaxed transition-all duration-500">
                    With a proven track record in ATL, BTL, and 360° brand activations, we have 
                    powered some of the biggest names in the market, including Sensodyne, Pepsi, and more.
                  </p>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed transition-all duration-500">
                    Our mission is simple: to transform every touchpoint into a powerful brand story.
                  </p>
                </div>
                
                {/* Corner Accents */}
                <div className="absolute top-4 right-4 w-2 h-2 bg-brand-orange/50 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-300"></div>
                <div className="absolute bottom-4 left-4 w-1.5 h-1.5 bg-brand-gold/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-500 delay-200"></div>
              </div>
            </div>

            {/* Achievements Grid */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              {achievements.map((achievement, index) => (
                <div key={achievement.label} className="text-center group">
                  <div className="bg-white/80 backdrop-blur-sm border border-white/30 rounded-2xl p-4 sm:p-6 transition-all duration-500 group-hover:shadow-lg group-hover:shadow-brand-orange/10 group-hover:scale-105">
                    <div className="text-2xl sm:text-3xl mb-2 group-hover:scale-110 transition-transform duration-300">
                      {achievement.icon}
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                      {achievement.number}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">
                      {achievement.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Right Content - Why Choose Us */}
          <div 
            ref={rightRef}
            className={`transition-all duration-1000 ${
              rightVisible 
                ? 'opacity-100 translate-x-0' 
                : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="relative overflow-hidden">
              {/* Card Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/30 to-brand-orange/30 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10 scale-110"></div>
              
              {/* Main Card */}
              <div className="relative bg-gradient-to-br from-brand-primary via-brand-secondary to-brand-orange rounded-3xl p-8 sm:p-10 text-white overflow-hidden">
                {/* Background Elements */}
                <div className="absolute top-6 left-6 w-16 h-16 bg-white/10 rounded-full blur-lg animate-pulse"></div>
                <div className="absolute bottom-6 right-6 w-12 h-12 bg-white/15 rounded-full blur-md animate-bounce"></div>
                <div className="absolute top-1/2 left-1/4 w-8 h-8 bg-white/5 rounded-full blur-xl animate-ping"></div>

                <div className="relative z-10">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4 shadow-lg">
                      🎯
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold mb-2 transition-all duration-500">
                      Why Brand Aviation?
                    </h3>
                  </div>
                  
                  <div className="space-y-6">
                    {whyChooseUs.map((item, index) => (
                      <div key={item.title} className="group flex items-start space-x-4 transition-all duration-300 hover:translate-x-2">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-2xl flex items-center justify-center text-xl sm:text-2xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 shadow-lg">
                          {item.icon}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-sm sm:text-base text-white group-hover:text-brand-orange transition-colors duration-300 mb-1">
                            {item.title}
                          </h4>
                          <p className="text-white/90 text-xs sm:text-sm leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                        <div className="w-2 h-2 bg-brand-orange rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-300"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced 3D Expertise Section */}
        <div 
          ref={expertiseRef}
          className={`relative overflow-hidden transition-all duration-1000 ${
            expertiseVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          {/* 3D Background with Depth */}
          <div className="relative bg-gradient-to-br from-brand-primary via-brand-secondary to-brand-primary rounded-3xl sm:rounded-[2rem] p-8 sm:p-12 lg:p-16 text-white overflow-hidden">
            {/* Animated 3D Grid */}
            <div className="absolute inset-0 opacity-20">
              <div className="h-full w-full" style={{
                backgroundImage: `
                  linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px),
                  linear-gradient(45deg, rgba(255,255,255,0.05) 1px, transparent 1px)
                `,
                backgroundSize: '60px 60px, 60px 60px, 30px 30px'
              }}></div>
            </div>

            {/* Floating 3D Elements */}
            <div className="absolute top-8 left-8 w-16 h-16 bg-brand-orange/30 rounded-full blur-lg animate-pulse"></div>
            <div className="absolute top-16 right-12 w-12 h-12 bg-brand-gold/40 rounded-full blur-md animate-bounce"></div>
            <div className="absolute bottom-12 left-16 w-20 h-20 bg-brand-orange/20 rounded-full blur-xl animate-ping"></div>
            <div className="absolute bottom-8 right-8 w-14 h-14 bg-brand-gold/35 rounded-full blur-lg animate-pulse"></div>

            {/* 3D Perspective Elements */}
            <div className="absolute top-1/4 left-1/4 w-8 h-8 bg-white/10 rounded-full animate-ping"></div>
            <div className="absolute top-3/4 right-1/4 w-6 h-6 bg-white/15 rounded-full animate-pulse"></div>
            <div className="absolute bottom-1/4 left-1/3 w-10 h-10 bg-white/5 rounded-full animate-bounce"></div>

            {/* Main Content */}
            <div className="relative z-10 text-center">
              {/* 3D Title with Gradient */}
              <div className="mb-8">
                <h3 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-brand-orange via-brand-gold to-brand-orange bg-clip-text text-transparent animate-pulse">
                  Our 3D Expertise
                </h3>
                <div className="w-24 h-1 bg-gradient-to-r from-brand-orange to-brand-gold mx-auto rounded-full animate-pulse"></div>
              </div>

              {/* Subtitle with 3D Effect */}
              <p className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-8 text-white drop-shadow-lg">
                Design that feels{' '}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-brand-orange to-brand-gold bg-clip-text text-transparent font-bold">
                    alive
                  </span>
                  <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-brand-orange to-brand-gold rounded-full animate-pulse"></div>
                </span>
                .
              </p>

              {/* Description */}
              <p className="text-base sm:text-lg lg:text-xl text-gray-200 max-w-4xl mx-auto mb-12 leading-relaxed">
                From 3D billboards to experiential mockups, we bring futuristic visuals to campaigns 
                that stop people in their tracks.
              </p>

              {/* 3D Feature Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12">
                {[
                  {
                    icon: '🎯',
                    title: '3D Billboards',
                    description: 'Immersive outdoor displays that command attention',
                    color: 'from-brand-orange/20 to-brand-gold/20'
                  },
                  {
                    icon: '🚀',
                    title: 'Experiential Mockups',
                    description: 'Interactive prototypes that bring ideas to life',
                    color: 'from-brand-gold/20 to-brand-orange/20'
                  },
                  {
                    icon: '✨',
                    title: 'Futuristic Visuals',
                    description: 'Cutting-edge designs that define tomorrow',
                    color: 'from-brand-orange/20 to-brand-gold/20'
                  }
                ].map((feature, index) => (
                  <div 
                    key={feature.title}
                    className="group relative"
                    style={{ animationDelay: `${index * 200}ms` }}
                  >
                    {/* Card Glow */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10 scale-110`}></div>
                    
                    {/* Card */}
                    <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 sm:p-8 text-center transition-all duration-500 group-hover:scale-105 group-hover:shadow-xl group-hover:shadow-brand-orange/25">
                      <div className="text-4xl sm:text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                        {feature.icon}
                      </div>
                      <h4 className="text-lg sm:text-xl font-bold text-white mb-3 group-hover:text-brand-orange transition-colors duration-300">
                        {feature.title}
                      </h4>
                      <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                        {feature.description}
                      </p>
                      
                      {/* Hover Effect Particles */}
                      <div className="absolute top-2 right-2 w-2 h-2 bg-brand-orange rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-300"></div>
                      <div className="absolute bottom-2 left-2 w-1.5 h-1.5 bg-brand-gold rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-500 delay-100"></div>
                    </div>
                  </div>
                ))}
              </div>

            </div>

            {/* 3D Corner Accents */}
            <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-brand-orange/50 rounded-tl-lg animate-pulse"></div>
            <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-brand-gold/50 rounded-tr-lg animate-pulse"></div>
            <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-brand-orange/50 rounded-bl-lg animate-pulse"></div>
            <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-brand-gold/50 rounded-br-lg animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
